// wijmo
import * as wjCore from "@grapecity/wijmo"
import * as wjGrid from "@grapecity/wijmo.grid"
import { FlexGridFilter } from "@grapecity/wijmo.grid.filter"
import CONST from "../../constant/"
import {
  TGridConditionData,
  TPageConditionData,
} from "../../type/pageConditionData"
import * as lodash from "lodash"

//#region [color2] PageSize

/**
 * グリッドのフィルター状態を保存する
 * @param pageSizeLayoutKey ローカルストレージのkey
 * @param ページサイズ 表示ページナンバー
 */
export const savePageSize = ({
  ページサイズ,
  pageSizeLayoutKey,
}: {
  ページサイズ: number
  pageSizeLayoutKey: string
}) => {
  //ローカルストレージよりレイアウトデータを取得
  localStorage.setItem(pageSizeLayoutKey, JSON.stringify(ページサイズ))
}

/**
 * ページサイズの復元
 * @param flexGridId FlexGridのHTML_ID
 * @param setPageSize ページサイズを保持するstateに対するset関数
 */
export const restorePageSize = (
  pageSizeLayoutKey: string,
  setPageSize: React.Dispatch<React.SetStateAction<number>>
) => {
  const currentData = localStorage.getItem(pageSizeLayoutKey)

  if (currentData !== "" && currentData != null) {
    setPageSize(+currentData)
  }
}

//#endregion

//#region [color1] GridFilterState

/**
 * 一致するTGridConditionDataを取得
 * @param pathName pathName
 * @param flexGridId GridId
 * @param createDefault デフォルトのDataを生成し返却するか
 * @returns 一致Data
 */
const getGridConditionData = (
  pathName: string,
  flexGridId: string,
  createDefault?: boolean
) => {
  const dataArrayJson = localStorage.getItem(
    CONST.LOCALSTORAGE_PROPERTIES.GRID_FILTER_DATA
  )
  let retValue: TGridConditionData | undefined = undefined

  if (dataArrayJson && dataArrayJson !== "") {
    const dataArray: Array<TGridConditionData> = JSON.parse(dataArrayJson)
    retValue = dataArray.find(
      (d) => d.pathName === pathName && d.flexGridId === flexGridId
    )
  }

  if (createDefault && !retValue) {
    retValue = {
      pathName,
      flexGridId,
      filterDefinition: "",
      pageNumber: 1,
      selectedRowIndex: -1,
      sortDescriptions: [],
    }
  }

  return retValue
}

/**
 * TGridConditionData保存処理
 * @param data 対象データ
 */
const saveGridConditionData = (data: TGridConditionData) => {
  const dataArrayJson = localStorage.getItem(
    CONST.LOCALSTORAGE_PROPERTIES.GRID_FILTER_DATA
  )

  // Jsonがない時
  if (!dataArrayJson || dataArrayJson === "") {
    localStorage.setItem(
      CONST.LOCALSTORAGE_PROPERTIES.GRID_FILTER_DATA,
      JSON.stringify([data])
    )
  } else {
    // Jsonがある時
    let dataArray: Array<TGridConditionData> = JSON.parse(dataArrayJson)

    // 同じIDを詰めなし
    lodash.remove(
      dataArray,
      (d) => d.pathName === data.pathName && d.flexGridId === data.flexGridId
    )
    dataArray.push(data)
    localStorage.setItem(
      CONST.LOCALSTORAGE_PROPERTIES.GRID_FILTER_DATA,
      JSON.stringify(dataArray)
    )
  }
}

/**
 * ローカルストレージに保存されているグリッドのフィルター状態、ページサイズ、表示ページを復元する
 * @param flexGridFilter flexGridFilter
 * @param flexGridId FlexGridのHTML_ID
 */
export const restoreGridFilterAndSortState = (
  locationPathName: string,
  flexGridFilter: FlexGridFilter,
  flexGridId: string
) => {
  return new Promise<void>((resolve, reject) => {
    try {
      // 対象Dataを取得
      const data = getGridConditionData(locationPathName, flexGridId)
      if (!data) {
        resolve()
        return
      }

      // Memo:リストア後のデータ削除はここでは行わない
      // Memo:複数のConditionを取り扱うので、利用側ですべて扱い切った後にremoveGridFilterStateを呼ぶこと

      // ソート条件復帰
      if (data.sortDescriptions?.length !== 0) {
        const gridFilterDescriptions = flexGridFilter.grid.itemsSource
          .sortDescriptions as wjCore.ObservableArray<wjCore.SortDescription>
        gridFilterDescriptions.beginUpdate()
        gridFilterDescriptions.clear()
        data.sortDescriptions.forEach((sd) => {
          gridFilterDescriptions.push(
            new wjCore.SortDescription(sd._bnd._key, sd._asc)
          )
        })
        gridFilterDescriptions.endUpdate()
      }

      // フィルタ適用後インナーハンドラを設定
      const innerHandler = (filter: FlexGridFilter) => {
        try {
          // 発火したらハンドル解除
          filter.filterApplied.removeHandler(innerHandler)

          // ページ遷移
          if (filter.grid.itemsSource) {
            filter.grid.itemsSource.moveToPage(data.pageNumber)
          }

          // grid内の行選択位置を復帰
          flexGridFilter.grid.select(data.selectedRowIndex, 0)
          resolve()
        } catch (err) {
          reject()
        }
      }

      // フィルタ復帰
      flexGridFilter.filterApplied.addHandler(innerHandler)
      flexGridFilter.filterDefinition = data.filterDefinition
    } catch (err) {
      reject()
    }
  })
}

/**
 * グリッドのフィルター状態を保存する
 * @param flexGridFilter flexGridFilter
 * @param pageNumber 表示ページナンバー
 */
export const saveGridFilterAndSortState = (
  locationPathName: string,
  flexGridFilter: FlexGridFilter
) => {
  // createDefaultしつつ取得
  let currentData = getGridConditionData(
    locationPathName,
    flexGridFilter.grid.hostElement.id,
    true
  ) as TGridConditionData

  currentData = {
    ...currentData,
    filterDefinition: flexGridFilter.filterDefinition, // フィルタ条件
    pageNumber: flexGridFilter.grid.itemsSource!.pageIndex,
    sortDescriptions: flexGridFilter.grid.itemsSource?.sortDescriptions?.map(
      (o: any) => o
    ),
    selectedRowIndex: flexGridFilter.grid.selection?.row, // 選択行インデックス
  }
  saveGridConditionData(currentData)
}

/**
 * グリッドのフィルター状態を保存する
 * @param locationPathName locationPathName
 * @param flexGrid flexGrid
 */
export const saveGridSortState = (
  locationPathName: string,
  flexGrid: wjGrid.FlexGrid
) => {
  // createDefaultしつつ取得
  let currentData = getGridConditionData(
    locationPathName,
    flexGrid.hostElement.id,
    true
  ) as TGridConditionData

  currentData = {
    ...currentData,
    pageNumber: flexGrid.itemsSource!.pageIndex,
    sortDescriptions: flexGrid.itemsSource?.sortDescriptions.map((o: any) => o),
    selectedRowIndex: flexGrid.selection?.row, // 選択行インデックス
  }

  saveGridConditionData(currentData)
}

/**
 * ローカルストレージに保存されている全てのフィルター状態を削除する
 */
export const removeAllGridFilterAndSortStates = () => {
  // 空配列で上書き
  localStorage.setItem(
    CONST.LOCALSTORAGE_PROPERTIES.GRID_FILTER_DATA,
    JSON.stringify([])
  )
}

//#endregion

//#region [color2] PageConditionData

// ページ状態の保存
/**
 * ページの縦スクロール位置をローカルストレージに保存する
 */
export const savePageConditionData = (locationPathName: string) => {
  // 縦のスクロール位置などを保存
  const element = document.getElementById("rootContainer")
  const scrollY = element?.scrollTop

  if (scrollY == null) return
  const filterData: TPageConditionData = {
    pageName: locationPathName,
    scrollY,
  }
  localStorage.setItem(
    CONST.LOCALSTORAGE_PROPERTIES.PAGE_CONDITION_DATA,
    JSON.stringify(filterData)
  )
}

/**
 * ページ状態の復元
 */
export const restorePageConditionData = (locationPathName: string) => {
  // ページ状態のデータを取得
  const currentData = localStorage.getItem(
    CONST.LOCALSTORAGE_PROPERTIES.PAGE_CONDITION_DATA
  )
  if (currentData !== "" && currentData != null) {
    const data: TPageConditionData = JSON.parse(currentData)
    if (data.pageName === locationPathName) {
      // 復元対象のパスかを確認
      const element = document.getElementById("rootContainer")
      element?.scrollTo({ top: data.scrollY })
    }
    // 一度復元した物、または他のページ遷移をした場合は削除する
    removePageConditionData()
  }
}

/**
 * ページ状態の削除
 */
export const removePageConditionData = () => {
  localStorage.removeItem(CONST.LOCALSTORAGE_PROPERTIES.PAGE_CONDITION_DATA)
}

const gridPageHelper = {
  savePageSize,
  restorePageSize,
  getGridConditionData,
  saveGridConditionData,
  restoreGridFilterAndSortState,
  saveGridFilterAndSortState,
  saveGridSortState,
  removeAllGridFilterAndSortStates,
  savePageConditionData,
  restorePageConditionData,
  removePageConditionData,
}

export default gridPageHelper
//#endregion
